import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { llmServiceFetcher } from '../LlmServiceFetcher';

const ONE_HOUR_MS = 1000 * 60 * 60;

export interface Prompt {
    key: string;
    displayName: string;
    content?: string;
}

export interface PromptsListResponse {
    prompts: Prompt[];
}

export interface PromptDetailResponse {
    key: string;
    displayName: string;
    content: string;
}

/**
 * Hook to fetch a specific prompt by key
 */
export const useGetPrompt = (promptKey: string) => {
    return useQuery<PromptDetailResponse, Error>({
        queryKey: ['prompts', promptKey],
        queryFn: async () => {
            return await llmServiceFetcher<PromptDetailResponse>({
                endpoint: `/prompts/${promptKey}`,
                method: 'GET',
            });
        },
        enabled: !!promptKey,
        staleTime: ONE_HOUR_MS,
    });
};

/**
 * Hook to update a prompt
 */
export const useUpdatePrompt = () => {
    const queryClient = useQueryClient();
    return useMutation<unknown, Error, { promptKey: string; content: string }>({
        mutationFn: async ({ promptKey, content }) => {
            await llmServiceFetcher({
                endpoint: `/prompts/${promptKey}`,
                method: 'PUT',
                body: JSON.stringify({ content }),
            });
        },
        onSuccess: (_data, variables) => {
            // Invalidate and refetch
            void queryClient.invalidateQueries({ queryKey: ['prompts'] });
            void queryClient.invalidateQueries({
                queryKey: ['prompts', variables.promptKey],
            });
        },
    });
};
